import { useEffect, useRef, useState } from 'react';
import { animate } from 'animejs';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useUserStore } from '@/stores/userStore';
import {
  broadcastListUnread,
  broadcastMarkRead,
  type Broadcast,
} from '@/services/broadcast';
import styles from './BroadcastReceiver.module.css';

const PRIORITY_LABEL: Record<Broadcast['priority'], string> = {
  normal: 'NORMAL',
  important: 'IMPORTANT',
  critical: 'CRITICAL · URGENT',
};

function BroadcastReceiver() {
  const isLoggedIn = useUserStore((s) => s.isLoggedIn);
  const userId = useUserStore((s) => s.userInfo?.id);
  const [queue, setQueue] = useState<Broadcast[]>([]);
  const [acking, setAcking] = useState(false);
  const scanLineRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);

  // 登录变化或用户切换时拉一次未读
  useEffect(() => {
    if (!isLoggedIn || !userId) {
      fetchedRef.current = false;
      setQueue([]);
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const list = await broadcastListUnread();
        setQueue(list);
      } catch {
        // ignore
      }
    })();
  }, [isLoggedIn, userId]);

  // 扫描线动画
  useEffect(() => {
    if (queue.length === 0 || !scanLineRef.current) return;
    const el = scanLineRef.current;
    const anim = animate(el, {
      top: ['0%', '100%'],
      opacity: [0.0, 0.85, 0.0],
      duration: 2400,
      ease: 'inOutQuad',
      loop: true,
    });
    return () => {
      anim.pause();
    };
  }, [queue.length, queue[0]?.id]);

  if (queue.length === 0) return null;

  const current = queue[0];
  const priorityClass =
    current.priority === 'critical'
      ? styles.priorityCritical
      : current.priority === 'important'
        ? styles.priorityImportant
        : styles.priorityNormal;

  const panelClass =
    current.priority === 'critical'
      ? styles.panelCritical
      : current.priority === 'important'
        ? styles.panelImportant
        : '';

  const handleAck = async () => {
    if (acking) return;
    setAcking(true);
    try {
      await broadcastMarkRead(current.id);
      window.dispatchEvent(new Event('broadcast:refresh-inbox'));
    } catch {
      // 忽略；继续展示下一条
    } finally {
      setAcking(false);
      setQueue((prev) => prev.slice(1));
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.stopPropagation()}>
      <div className={`${styles.panel} ${panelClass}`}>
        <span className={`${styles.corner} ${styles.tl}`} />
        <span className={`${styles.corner} ${styles.tr}`} />
        <span className={`${styles.corner} ${styles.bl}`} />
        <span className={`${styles.corner} ${styles.br}`} />
        <div ref={scanLineRef} className={styles.scanLine} />

        <div className={styles.header}>
          <div className={styles.headerTop}>
            <span className={styles.brand}>STARSHIP · BROADCAST</span>
            <span className={`${styles.priorityBadge} ${priorityClass}`}>
              {PRIORITY_LABEL[current.priority]}
            </span>
          </div>
          <div className={styles.title}>{current.title}</div>
          <div className={styles.meta}>
            ◇ {dayjs(current.createdAt).format('YYYY-MM-DD HH:mm')}
            {current.senderName ? `  ◇ FROM ${current.senderName}` : ''}
          </div>
        </div>

        <div className={styles.content}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {current.contentMd}
          </ReactMarkdown>
        </div>

        <div className={styles.footer}>
          <span className={styles.queueHint}>
            {queue.length > 1 ? `还有 ${queue.length - 1} 条待接收` : '已是最后一条'}
          </span>
          <button
            className={`${styles.ackBtn} ${current.priority === 'critical' ? styles.ackBtnCritical : ''}`}
            onClick={handleAck}
            disabled={acking}
          >
            {acking ? '处理中…' : '已收到'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BroadcastReceiver;
